/**
 * Recovering from a deploy that landed while someone had the app open.
 *
 * Next.js fingerprints its chunk filenames per build. A page loaded before a
 * deploy goes looking for chunks the new build no longer serves, and any
 * lazy-loaded module — a dialog, a PDF library, the offer core — fails the
 * moment it is needed. The browser reports "Loading chunk 9981 failed".
 *
 * Field report, 2026-10-09: an admin building a gig for performers not yet on
 * the roster pressed "Save & add another performer" and got the chunk URL
 * printed at her. We had deployed four times that day. The work was not saved
 * and the button stayed dead.
 *
 * There was already a global handler for this, but it listened only for
 * UNCAUGHT errors. The submit path wraps its work in try/catch and shows
 * `e.message` in a toast, so the error was swallowed before the handler could
 * see it and the recovery never ran. These helpers are shared so the global
 * listener and the toast layer agree on what a chunk failure looks like and
 * do the same thing about it.
 */

const RELOAD_KEY = 'chunk-load-recovery-attempted';

/**
 * Does this error (or message string) describe a chunk that could not load?
 *
 * Matches on text because the shape varies by browser and by how far the error
 * travelled: a real ChunkLoadError, a bare message pulled off `e.message` by a
 * catch block, or the native "Failed to fetch dynamically imported module".
 */
export function isChunkLoadError(value: unknown): boolean {
  if (!value) return false;

  let text = '';
  if (typeof value === 'string') {
    text = value;
  } else if (typeof value === 'object') {
    const e = value as {
      name?: string;
      message?: string;
      reason?: { name?: string; message?: string };
    };
    text = [e.name, e.message, e.reason?.name, e.reason?.message].filter(Boolean).join(' ');
  } else {
    return false;
  }

  const t = text.toLowerCase();
  return (
    t.includes('chunkloaderror') ||
    t.includes('loading chunk') ||
    t.includes('failed to fetch dynamically imported module') ||
    t.includes('error loading dynamically imported module') ||
    // Safari's wording when a module script 404s after a deploy.
    t.includes('importing a module script failed')
  );
}

/**
 * Reload, at most once per session.
 *
 * The guard matters: if the reload lands on a build that is still rotating, a
 * second failure would otherwise put the tab in a refresh loop and the user
 * could never read the message telling them what happened.
 */
export function recoverFromChunkError(delayMs = 0): void {
  if (typeof window === 'undefined') return;

  try {
    if (window.sessionStorage.getItem(RELOAD_KEY) === '1') {
      window.sessionStorage.removeItem(RELOAD_KEY);
      return;
    }
    window.sessionStorage.setItem(RELOAD_KEY, '1');
  } catch {
    // Private browsing or blocked storage — still worth trying to recover.
  }

  if (delayMs > 0) {
    window.setTimeout(() => window.location.reload(), delayMs);
  } else {
    window.location.reload();
  }
}

/** Cleared on a successful mount, so the next genuine failure can recover. */
export function clearChunkRecoveryFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Ignore storage access issues.
  }
}

/** What a person should be told, instead of a chunk filename. */
export const CHUNK_RECOVERY_MESSAGE =
  'FlowStage just updated in the background. Reloading this page — your draft is saved, so you can pick up where you left off.';
